import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Navbar } from "../components/layout/Navbar";
import { useAuth } from "../context/auth";
import { Sparkles, Eye, EyeOff } from "lucide-react";

const API_BASE_URL = process.env.REACT_APP_API_URL;

function isValidPassword(password) {
  return (
    password.length >= 8 &&
    /[A-Z]/.test(password) &&
    /[0-9]/.test(password) &&
    /[^A-Za-z0-9]/.test(password)
  );
}

export default function GetStartedPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();

  const initialTab = useMemo(() => {
    const hash = (location.hash || "").replace("#", "");
    return hash === "signup" ? "signup" : "login";
  }, [location.hash]);

  const [tab, setTab] = useState(initialTab);
  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [name, setName] = useState("");
  const [preferredStyle, setPreferredStyle] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = (mode) => async (e) => {
    e.preventDefault();
    setError("");

    const safeEmail = email.trim();
    const safeName = name.trim();
    const safeStyle = preferredStyle.trim();

    if (!safeEmail || !password) {
      setError("Email and password are required.");
      return;
    }

    if (mode === "signup") {
      if (password !== confirmPassword) {
        setError("Passwords do not match.");
        return;
      }
      if (!isValidPassword(password)) {
        setError(
          "Password must be at least 8 characters and include an uppercase letter, a number, and a special character."
        );
        return;
      }
    }

    if (!API_BASE_URL) {
      setError("API base URL is not configured.");
      return;
    }

    try {
      setIsSubmitting(true);

      const endpoint =
        mode === "login"
          ? `${API_BASE_URL}/auth/login`
          : `${API_BASE_URL}/auth/signup`;

      const payload =
        mode === "login"
          ? { email: safeEmail, password }
          : {
              email: safeEmail,
              password,
              displayName: safeName || undefined,
              styleTrack: safeStyle || undefined,
            };

      const response = await fetch(endpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        const message =
          data?.message || data?.error || "Unable to authenticate. Please try again.";
        throw new Error(message);
      }

      const data = await response.json().catch(() => ({}));

      if (mode === "signup") {
        navigate("/verify", { replace: true, state: { email: safeEmail } });
        return;
      }

      const userPayload =
        data.user || {
          id: data.id || crypto?.randomUUID?.() || String(Date.now()),
          email: data.email || safeEmail,
          displayName: data.displayName || safeName || "Artist",
        };

      const idToken = data.idToken || data.token || data.accessToken;
      const refreshToken = data.refreshToken;

      signIn({
        ...userPayload,
        idToken,
        refreshToken,
      });

      navigate("/dashboard/onboarding/questionnaire", { replace: true });
    } catch (err) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[hsl(220,20%,97%)]" data-testid="get-started-page">
      <Navbar />

      <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-lg mx-auto">
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-sm border border-slate-200/60 mb-4">
              <Sparkles className="w-4 h-4 text-[hsl(234,89%,63%)]" />
              <span className="text-sm font-medium text-slate-600">Get Started</span>
            </div>
            <h1 className="text-3xl font-bold text-[hsl(222,47%,11%)] font-['Outfit']">
              Login or create an account
            </h1>
            <p className="text-slate-600 mt-2">
              Sign in to access your dashboard and complete onboarding.
            </p>
          </div>

          <Card className="card-elevated border-0">
            <CardHeader className="pb-3">
              <CardTitle className="font-['Outfit']">Welcome</CardTitle>
              <CardDescription>Choose login or sign up to continue.</CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={tab} onValueChange={setTab}>
                <TabsList className="bg-slate-100 p-1 rounded-full w-full">
                  <TabsTrigger value="login" className="rounded-full w-full">
                    Login
                  </TabsTrigger>
                  <TabsTrigger value="signup" className="rounded-full w-full">
                    Sign up
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="login" className="mt-6">
                  <form onSubmit={onSubmit("login")} className="space-y-4" data-testid="login-form">
                    <div className="space-y-2">
                      <Label htmlFor="login-email">Email</Label>
                      <Input
                        id="login-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </div>
                    <div className="space-y-2 relative">
                      <Label htmlFor="login-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="login-password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="••••••••"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white rounded-full h-11 disabled:opacity-60"
                      data-testid="login-submit"
                    >
                      {isSubmitting ? "Signing in..." : "Continue"}
                    </Button>
                  </form>
                </TabsContent>

                <TabsContent value="signup" className="mt-6">
                  <form onSubmit={onSubmit("signup")} className="space-y-4" data-testid="signup-form">
                    <div className="space-y-2">
                      <Label htmlFor="signup-name">Name</Label>
                      <Input
                        id="signup-name"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Your name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-style">Preferred art style</Label>
                      <Input
                        id="signup-style"
                        value={preferredStyle}
                        onChange={(e) => setPreferredStyle(e.target.value)}
                        placeholder="e.g. Realism, Anime/Manga, Concept Art"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-email">Email</Label>
                      <Input
                        id="signup-email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-password">Password</Label>
                      <div className="relative">
                        <Input
                          id="signup-password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          placeholder="Create a password"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                        >
                          {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="signup-confirm-password">Confirm Password</Label>
                      <div className="relative">
                        <Input
                          id="signup-confirm-password"
                          type={showConfirmPassword ? "text" : "password"}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          placeholder="Confirm your password"
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none"
                        >
                          {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>

                    <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-[hsl(234,89%,63%)] hover:bg-[hsl(234,89%,58%)] text-white rounded-full h-11 disabled:opacity-60"
                      data-testid="signup-submit"
                    >
                      {isSubmitting ? "Creating account..." : "Create account"}
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>

              {error && (
                <p className="text-xs text-red-500 mt-4 text-center" data-testid="auth-error">
                  {error}
                </p>
              )}

              <p className="text-xs text-slate-500 mt-6 text-center">
                By continuing you agree to our <Link to="#" className="hover:underline">Terms</Link>.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

